// Student portal — companion module.
// Rich, aesthetic modals and cards used by the Student Dashboard:
//   • QuickActionsRich        – 6-tile action grid, each opens a stunning modal
//   • BusCheckInDialog        – detailed on-bus check-in with route picker
//   • WellbeingCard           – daily mood + mental-health screener (PHQ-2 / GAD-2)
//   • ClubsBoard              – live clubs from DB, "Join" opens a rich modal
//   • MealsServiceCardV2      – daily menu + order/opt-in from DB
//   • LibraryLabCardV2        – bookings (lab / study lounge / maker space) from DB
//   • ServicesHub             – new-request builder + document uploads (storage)
//   • FundredPayCardShowcase  – physical card carousel with front/back + activation
//   • StudyLibrary            – learning resources (Learn tab)
//
// All persistence goes through public tables shipped in the migration bundle:
//   student_service_requests, student_service_documents, school_clubs,
//   student_club_memberships, student_wellbeing_checks, library_lab_bookings,
//   student_meal_orders, student_payment_cards, learning_resources.

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import QRCode from "qrcode";
import {
  Wallet, QrCode, MessageSquare, FileText, Bus, Send, Sparkles, Copy,
  Utensils, Library, FlaskConical, Lightbulb, Coffee, Leaf, PlayCircle,
  Music, PartyPopper, Trophy, Star, Users, MapPin, Clock, HeartPulse,
  Smile, Frown, Meh, Cloud, Sun, Moon, Zap, ShieldCheck, CheckCircle2,
  XCircle, Hourglass, Plus, Upload, Paperclip, Trash2, ChevronRight,
  ClipboardCheck, Radio, Flame, Rocket, BookOpen, Video, Download,
  CreditCard, Fingerprint, Lock, Camera, Stethoscope, Brain, HeartHandshake,
  Palette, Beaker, Wrench, Volleyball, Loader2, ArrowUpRight, ExternalLink,
  ScanLine, Home, GraduationCap, Award, Timer,
} from "lucide-react";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { fmtUGX, shortDateTime } from "@/lib/format";
import fundredCard from "@/assets/fundred-pay-card.jpg";

// ═════════════════════════════════════════════════════════════════════════════
// QUICK ACTIONS ─ 6 tiles, each opens a rich modal
// ═════════════════════════════════════════════════════════════════════════════

export function QuickActionsRich({ wallet }: { wallet: any }) {
  const tiles = [
    { key: "pay",       Icon: QrCode,        label: "Pay QR",       hue: "#7B1416", sub: "Show at canteen"    },
    { key: "ask",       Icon: Wallet,        label: "Ask guardian", hue: "#5B4B8A", sub: "Request pocket money" },
    { key: "homework",  Icon: FileText,      label: "Homework",     hue: "#C1553A", sub: "Upload · submit"     },
    { key: "message",   Icon: MessageSquare, label: "Message",      hue: "#0ea5e9", sub: "Teacher · house"     },
    { key: "wellbeing", Icon: HeartPulse,    label: "Wellbeing",    hue: "#B7355A", sub: "Talk to nurse"       },
    { key: "help",      Icon: ShieldCheck,   label: "Help / SOS",   hue: "#111114", sub: "Anonymous"           },
  ];
  const [open, setOpen] = useState<string | null>(null);
  return (
    <>
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground">Quick actions</div>
          <div className="text-[10px] text-muted-foreground/70">tap to open</div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {tiles.map(t => (
            <button
              key={t.key}
              onClick={() => setOpen(t.key)}
              className="group relative overflow-hidden rounded-xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md"
              style={{ background: `linear-gradient(135deg, ${t.hue}0d, transparent 60%)` }}
            >
              <div className="grid h-8 w-8 place-items-center rounded-lg transition group-hover:scale-110"
                style={{ background: `${t.hue}22`, color: t.hue }}>
                <t.Icon className="h-4 w-4" />
              </div>
              <div className="mt-2 text-[11px] font-semibold">{t.label}</div>
              <div className="text-[9px] text-muted-foreground">{t.sub}</div>
            </button>
          ))}
        </div>
      </Card>

      <PayQrModal    open={open === "pay"}       onOpenChange={(o: boolean) => !o && setOpen(null)} wallet={wallet} />
      <AskGuardianModal open={open === "ask"}    onOpenChange={(o: boolean) => !o && setOpen(null)} />
      <HomeworkModal open={open === "homework"}  onOpenChange={(o: boolean) => !o && setOpen(null)} />
      <MessageModal  open={open === "message"}   onOpenChange={(o: boolean) => !o && setOpen(null)} />
      <WellbeingScreenerModal open={open === "wellbeing"} onOpenChange={(o: boolean) => !o && setOpen(null)} />
      <HelpSosModal  open={open === "help"}      onOpenChange={(o: boolean) => !o && setOpen(null)} />
    </>
  );
}

// ─── Pay QR modal ───────────────────────────────────────────────────────────
function PayQrModal({ open, onOpenChange, wallet }: any) {
  const [qr, setQr] = useState("");
  const [amount, setAmount] = useState("");
  useEffect(() => {
    if (!open || !wallet?.id) return;
    const payload = amount
      ? JSON.stringify({ w: wallet.id, a: Number(amount) })
      : wallet.id;
    QRCode.toDataURL(payload, { margin: 1, width: 480, color: { dark: "#0F2E21", light: "#FBFAF5" } }).then(setQr);
  }, [open, wallet?.id, amount]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md overflow-hidden p-0">
        <div className="relative overflow-hidden p-6 text-white" style={{ background: "linear-gradient(135deg,#0F2E21,#7B1416 70%,#3d7a5c)" }}>
          <DialogHeader>
            <DialogTitle className="text-white">Pay by QR</DialogTitle>
            <DialogDescription className="text-white/70">Show this to a school-approved merchant.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid place-items-center">
            <div className="rounded-2xl bg-[#FBFAF5] p-3 shadow-2xl">
              {qr ? <img src={qr} alt="QR" className="h-56 w-56" /> : <div className="h-56 w-56 animate-pulse rounded bg-black/10" />}
            </div>
          </div>
          <div className="mt-4 rounded-xl border border-white/20 bg-white/10 p-3 backdrop-blur">
            <Label className="text-[10px] uppercase tracking-wider text-white/70">Optional amount (UGX)</Label>
            <Input value={amount} onChange={e => setAmount(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g. 3000" className="mt-1 border-white/30 bg-transparent text-white placeholder:text-white/40" />
            <div className="mt-2 text-[10px] text-white/60">Pre-fill so the cashier just approves.</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2 p-4 text-[11px]">
          <button className="rounded-lg border p-2 hover:bg-muted"><ScanLine className="mx-auto h-4 w-4" />Tap card</button>
          <button className="rounded-lg border p-2 hover:bg-muted"><Fingerprint className="mx-auto h-4 w-4" />Bio unlock</button>
          <button onClick={() => { navigator.clipboard.writeText(wallet?.id ?? ""); toast.success("Wallet ID copied"); }}
            className="rounded-lg border p-2 hover:bg-muted"><Copy className="mx-auto h-4 w-4" />Copy ID</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Ask guardian modal ────────────────────────────────────────────────────
function AskGuardianModal({ open, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [urgency, setUrgency] = useState("normal");
  const quick = [2000, 5000, 10000, 20000];
  const submit = useMutation({
    mutationFn: async () => {
      const amt = Number(amount || 0);
      if (!amt || !reason) throw new Error("Amount and reason are required");
      const { error } = await (supabase.from as any)("allowance_requests").insert({
        student_user_id: user!.id, amount_ugx: amt, reason, status: "pending",
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Sent to your guardian"); qc.invalidateQueries({ queryKey: ["student-requests"] }); onOpenChange(false); setAmount(""); setReason(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wallet className="h-4 w-4 text-primary" />Ask your guardian for money</DialogTitle>
          <DialogDescription>They will see the request on their Family Wallet and can approve in one tap.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount (UGX)</Label>
            <Input type="number" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1" />
            <div className="mt-2 flex flex-wrap gap-1.5">
              {quick.map(v => (
                <Button key={v} size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setAmount(String(v))}>+{fmtUGX(v)}</Button>
              ))}
            </div>
          </div>
          <div>
            <Label>What is it for?</Label>
            <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Science project materials" className="mt-1" />
          </div>
          <div>
            <Label>Urgency</Label>
            <Select value={urgency} onValueChange={setUrgency}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Whenever</SelectItem>
                <SelectItem value="normal">Today</SelectItem>
                <SelectItem value="urgent">Now — I'm at the counter</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Homework modal (creates service request of type homework) ─────────────
function HomeworkModal({ open, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [subject, setSubject] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const submit = useMutation({
    mutationFn: async () => {
      if (!title) throw new Error("Give it a title");
      const { data: req, error } = await (supabase.from as any)("student_service_requests").insert({
        student_user_id: user!.id, request_type: "homework", title, body: notes,
        metadata: { subject },
      }).select().single();
      if (error) throw error;
      for (const f of files) {
        const path = `${user!.id}/homework/${req.id}/${Date.now()}-${f.name}`;
        const up = await supabase.storage.from("student-documents").upload(path, f);
        if (up.error) throw up.error;
        await (supabase.from as any)("student_service_documents").insert({
          student_user_id: user!.id, request_id: req.id, doc_kind: "homework",
          file_name: f.name, storage_path: path, mime_type: f.type, size_bytes: f.size,
        });
      }
    },
    onSuccess: () => { toast.success("Homework submitted"); qc.invalidateQueries({ queryKey: ["student-service-requests"] }); onOpenChange(false); setTitle(""); setNotes(""); setFiles([]); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-4 w-4 text-primary" />Submit homework</DialogTitle>
          <DialogDescription>Upload documents or photos. Your teacher sees it instantly.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Subject</Label><Input value={subject} onChange={e => setSubject(e.target.value)} className="mt-1" placeholder="e.g. Physics" /></div>
          <div><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1" placeholder="e.g. Motion assignment 3" /></div>
          <div><Label>Notes for teacher</Label><Textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} className="mt-1" /></div>
          <FileDropZone files={files} setFiles={setFiles} />
        </div>
        <DialogFooter>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Upload className="mr-1 h-3 w-3" />}Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Message modal ─────────────────────────────────────────────────────────
function MessageModal({ open, onOpenChange }: any) {
  const { user } = useAuth();
  const [to, setTo] = useState("class_teacher");
  const [body, setBody] = useState("");
  const send = useMutation({
    mutationFn: async () => {
      if (!body) throw new Error("Write a message first");
      await (supabase.from as any)("student_service_requests").insert({
        student_user_id: user!.id, request_type: "message", title: `Message to ${to}`, body, metadata: { channel: to },
      });
    },
    onSuccess: () => { toast.success("Sent"); onOpenChange(false); setBody(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MessageSquare className="h-4 w-4 text-primary" />New message</DialogTitle>
          <DialogDescription>Reach a teacher, dorm parent, nurse or the office.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Send to</Label>
            <Select value={to} onValueChange={setTo}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="class_teacher">Class teacher</SelectItem>
                <SelectItem value="dorm_parent">Dorm parent</SelectItem>
                <SelectItem value="nurse">Nurse / Sick bay</SelectItem>
                <SelectItem value="counsellor">Counsellor</SelectItem>
                <SelectItem value="office">Front office</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Message</Label><Textarea rows={4} value={body} onChange={e => setBody(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => send.mutate()} disabled={send.isPending}><Send className="mr-1 h-3 w-3" />Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Help/SOS modal ────────────────────────────────────────────────────────
function HelpSosModal({ open, onOpenChange }: any) {
  const { user } = useAuth();
  const [anon, setAnon] = useState(true);
  const [body, setBody] = useState("");
  const submit = useMutation({
    mutationFn: async () => {
      if (!body) throw new Error("Tell us what's happening");
      await (supabase.from as any)("student_service_requests").insert({
        student_user_id: user!.id, request_type: "sos", title: "Help / SOS", body, priority: "urgent",
        metadata: { anonymous: anon },
      });
    },
    onSuccess: () => { toast.success("The safeguarding lead is being notified"); onOpenChange(false); setBody(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive"><ShieldCheck className="h-4 w-4" />Help / SOS</DialogTitle>
          <DialogDescription>Say what's going on. Your safeguarding lead is on standby.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea rows={5} value={body} onChange={e => setBody(e.target.value)} placeholder="You can be brief — someone will follow up privately." />
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} />
            Send anonymously (name hidden from initial view)
          </label>
        </div>
        <DialogFooter>
          <Button variant="destructive" onClick={() => submit.mutate()} disabled={submit.isPending}><ShieldCheck className="mr-1 h-3 w-3" />Send now</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// BUS CHECK-IN — detailed modal (routes, direction, seat, geo)
// ═════════════════════════════════════════════════════════════════════════════

export function BusCheckInDialog({ schedule = [], size = "sm" }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [route, setRoute] = useState("");
  const [direction, setDirection] = useState(new Date().getHours() < 12 ? "am" : "pm");
  const [stop, setStop] = useState("");
  const [seat, setSeat] = useState("");
  const [feeling, setFeeling] = useState("good");
  const [note, setNote] = useState("");

  const routes = useMemo(() => {
    const s = new Set<string>();
    (schedule || []).forEach((r: any) => r?.route && s.add(r.route));
    if (!s.size) ["Bus 14 · Naalya", "Bus 21 · Ntinda", "Bus 07 · Kira"].forEach(v => s.add(v));
    return Array.from(s);
  }, [schedule]);

  useEffect(() => { if (!route && routes[0]) setRoute(routes[0]); }, [routes, route]);

  const submit = useMutation({
    mutationFn: async () => {
      let coords: any = null;
      try {
        coords = await new Promise((res, rej) => {
          if (!navigator.geolocation) return res(null);
          navigator.geolocation.getCurrentPosition(p => res({ lat: p.coords.latitude, lng: p.coords.longitude }), () => res(null), { timeout: 3000 });
        });
      } catch { /* ignore */ }
      await (supabase.from as any)("bus_check_ins").insert({
        student_user_id: user!.id, route, direction, stop: stop || (direction === "am" ? "Home stop" : "School gate"),
        location: `On ${route} · seat ${seat || "—"} · ${feeling}`,
        meta: { seat, feeling, note, coords },
      });
    },
    onSuccess: () => { toast.success("Checked in ✓"); qc.invalidateQueries({ queryKey: ["student-bus"] }); setOpen(false); setNote(""); },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size={size as any} variant="outline"><Bus className="mr-1 h-3 w-3" />Check in</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md overflow-hidden p-0">
        <div className="relative overflow-hidden p-5 text-white" style={{ background: "linear-gradient(135deg,#0f2e21,#7B1416,#3d7a5c)" }}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-3xl" />
          <DialogHeader>
            <DialogTitle className="text-white">Board my bus</DialogTitle>
            <DialogDescription className="text-white/70">Live location and headcount will update instantly.</DialogDescription>
          </DialogHeader>
          <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
            {["am","pm"].map(d => (
              <button key={d} onClick={() => setDirection(d)}
                className={`rounded-xl border px-3 py-2 uppercase tracking-widest transition ${direction === d ? "border-white/70 bg-white/20" : "border-white/20 bg-white/5 opacity-70"}`}>
                {d === "am" ? "Home → School" : "School → Home"}
              </button>
            ))}
          </div>
        </div>
        <div className="space-y-3 p-5">
          <div>
            <Label>Route</Label>
            <Select value={route} onValueChange={setRoute}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="Pick your bus" /></SelectTrigger>
              <SelectContent>{routes.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Stop</Label><Input value={stop} onChange={e => setStop(e.target.value)} className="mt-1" placeholder="e.g. Naalya gate" /></div>
            <div><Label>Seat</Label><Input value={seat} onChange={e => setSeat(e.target.value)} className="mt-1" placeholder="e.g. 12B" /></div>
          </div>
          <div>
            <Label>How are you feeling?</Label>
            <div className="mt-1 flex gap-2">
              {[{k:"good",e:"🙂"},{k:"tired",e:"😴"},{k:"sick",e:"🤒"},{k:"anxious",e:"😟"}].map(f => (
                <button key={f.k} onClick={() => setFeeling(f.k)}
                  className={`flex-1 rounded-lg border p-2 text-lg transition ${feeling === f.k ? "border-primary bg-primary/10" : "hover:border-primary/40"}`}>
                  {f.e}
                </button>
              ))}
            </div>
          </div>
          <div><Label>Note to matron (optional)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter className="px-5 pb-5">
          <Button onClick={() => submit.mutate()} disabled={submit.isPending} className="w-full">
            {submit.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <MapPin className="mr-1 h-3 w-3" />}
            Confirm boarding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// WELLBEING — with PHQ-2 / GAD-2 style screener
// ═════════════════════════════════════════════════════════════════════════════

export function WellbeingCard({ health = [] }: { health: any[] }) {
  const [open, setOpen] = useState(false);
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg"><HeartPulse className="h-4 w-4 text-primary" />Wellbeing</div>
        <Badge variant="secondary" className="text-[10px]"><Brain className="mr-1 h-3 w-3" />Mental health</Badge>
      </div>
      <div className="rounded-xl bg-emerald-500/10 p-3 text-xs text-emerald-700 dark:text-emerald-400">
        <Smile className="mr-1 inline h-3 w-3" />You matter. Do a 30-second check-in.
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <Button size="sm" onClick={() => setOpen(true)}><Brain className="mr-1 h-3 w-3" />Screener</Button>
        <Button size="sm" variant="outline"><Stethoscope className="mr-1 h-3 w-3" />See nurse</Button>
      </div>
      <div className="mt-3 space-y-1.5 text-xs">
        {health.length === 0 && <div className="text-muted-foreground">No sick bay visits.</div>}
        {health.slice(0, 3).map((h: any) => (
          <div key={h.id} className="rounded-lg border p-2">
            <div className="font-semibold">{h.reason}</div>
            <div className="text-[10px] text-muted-foreground">{shortDateTime(h.visit_date)} · {h.status}</div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-center justify-between rounded-lg border p-2 text-[11px]">
        <span className="flex items-center gap-1.5"><HeartHandshake className="h-3.5 w-3.5 text-rose-500" />Anonymous helpline</span>
        <a className="text-primary hover:underline" href="tel:0800100200">0800 100 200</a>
      </div>
      <WellbeingScreenerModal open={open} onOpenChange={setOpen} />
    </Card>
  );
}

function WellbeingScreenerModal({ open, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [mood, setMood] = useState("good");
  const [sleep, setSleep] = useState("7");
  const [stress, setStress] = useState(3);
  const [q1, setQ1] = useState(0);
  const [q2, setQ2] = useState(0);
  const [q3, setQ3] = useState(0);
  const [q4, setQ4] = useState(0);
  const [note, setNote] = useState("");
  const [wantsTalk, setWantsTalk] = useState(false);

  const score = q1 + q2 + q3 + q4;
  const severity = score <= 2 ? "green" : score <= 5 ? "amber" : "red";
  const severityLabel = { green: "Doing well", amber: "Keep an eye", red: "Please talk to someone today" }[severity];

  const submit = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase.from as any)("student_wellbeing_checks").insert({
        student_user_id: user!.id, mood, sleep_hours: Number(sleep), stress_level: stress,
        phq2_little_interest: q1, phq2_feeling_down: q2, gad2_nervous: q3, gad2_worry: q4,
        screener_score: score, severity, free_note: note, wants_talk: wantsTalk,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Thanks for checking in"); qc.invalidateQueries({ queryKey: ["student-wellbeing"] }); onOpenChange(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  const OPTS = [["Not at all",0],["A few days",1],["More than half",2],["Nearly every day",3]] as const;
  const Question = ({ q, val, setVal }: any) => (
    <div>
      <div className="text-xs font-medium">{q}</div>
      <div className="mt-1 grid grid-cols-4 gap-1">
        {OPTS.map(([label, v]) => (
          <button key={label} onClick={() => setVal(v)}
            className={`rounded-lg border p-2 text-[10px] transition ${val === v ? "border-primary bg-primary/10 font-semibold" : "hover:border-primary/40"}`}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Brain className="h-4 w-4 text-primary" />Mental health check-in</DialogTitle>
          <DialogDescription>Just for you. School nurse & counsellor only see it if the score is high or you ask to talk.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-xs">
            {[{k:"great",e:"😄"},{k:"good",e:"🙂"},{k:"meh",e:"😐"},{k:"low",e:"😔"},{k:"sad",e:"😢"},{k:"sick",e:"🤒"}].map(m => (
              <button key={m.k} onClick={() => setMood(m.k)}
                className={`flex flex-col items-center gap-1 rounded-lg border p-2 transition ${mood === m.k ? "border-primary bg-primary/10" : "hover:border-primary/40"}`}>
                <div className="text-2xl">{m.e}</div>
                <div className="capitalize">{m.k}</div>
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>Sleep last night (hours)</Label><Input type="number" value={sleep} onChange={e => setSleep(e.target.value)} className="mt-1" min={0} max={16} /></div>
            <div>
              <Label>Stress level ({stress}/10)</Label>
              <input type="range" min={0} max={10} value={stress} onChange={e => setStress(Number(e.target.value))} className="mt-2 w-full accent-primary" />
            </div>
          </div>

          <div className="space-y-3 rounded-xl border bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Over the last two weeks…</div>
            <Question q="Little interest or pleasure in doing things"       val={q1} setVal={setQ1} />
            <Question q="Feeling down, depressed or hopeless"               val={q2} setVal={setQ2} />
            <Question q="Feeling nervous, anxious or on edge"               val={q3} setVal={setQ3} />
            <Question q="Not being able to stop or control worrying"        val={q4} setVal={setQ4} />
          </div>

          <div className={`rounded-xl p-3 text-xs ${severity === "green" ? "bg-emerald-500/10 text-emerald-700" : severity === "amber" ? "bg-amber-500/10 text-amber-700" : "bg-rose-500/10 text-rose-700"}`}>
            <div className="font-semibold">Score {score}/12 · {severityLabel}</div>
            {severity !== "green" && <div className="mt-1">Talking to someone helps. Tick below and a counsellor will reach out privately.</div>}
          </div>

          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={wantsTalk} onChange={e => setWantsTalk(e.target.checked)} />
            I'd like to talk to a counsellor
          </label>

          <div><Label>Anything you want to add? (private)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <HeartPulse className="mr-1 h-3 w-3" />}Save my check-in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// CLUBS — DB-loaded board with rich Join modal
// ═════════════════════════════════════════════════════════════════════════════

const clubIcon = (cat: string) => ({
  arts: Palette, academic: BookOpen, sport: Volleyball, stem: Beaker, general: Star,
} as Record<string, any>)[cat] || Star;

export function ClubsBoard() {
  const { user } = useAuth();
  const { data: clubs = [] } = useQuery({
    queryKey: ["student-clubs"],
    queryFn: async () => (await (supabase.from as any)("school_clubs").select("*").eq("active", true).order("name")).data ?? [],
  });
  const { data: memberships = [] } = useQuery({
    queryKey: ["student-club-memberships", user?.id],
    enabled: !!user?.id,
    queryFn: async () => (await (supabase.from as any)("student_club_memberships").select("*").eq("student_user_id", user!.id)).data ?? [],
  });
  const memberOf = new Set(memberships.map((m: any) => m.club_id));
  const [pick, setPick] = useState<any>(null);
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 font-display text-lg"><Star className="h-4 w-4 text-primary" />Squads & clubs</div>
        <Badge variant="secondary" className="text-[10px]">{clubs.length} open</Badge>
      </div>
      <div className="grid gap-2">
        {clubs.slice(0, 6).map((c: any) => {
          const Icon = clubIcon(c.category);
          const joined = memberOf.has(c.id);
          return (
            <div key={c.id} className="group flex items-center justify-between rounded-xl border p-2.5 transition hover:shadow-sm"
              style={{ background: `linear-gradient(90deg, ${c.color_hex}10, transparent)` }}>
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg" style={{ background: `${c.color_hex}22`, color: c.color_hex }}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <div className="text-xs font-semibold">{c.name}</div>
                  <div className="text-[10px] text-muted-foreground">{c.meets_on} · {String(c.meets_at ?? "").slice(0,5)} · {c.location}</div>
                </div>
              </div>
              {joined
                ? <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">Joined</Badge>
                : <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setPick(c)}>Join <ChevronRight className="ml-0.5 h-3 w-3" /></Button>}
            </div>
          );
        })}
        {clubs.length === 0 && <div className="text-xs text-muted-foreground">No clubs yet — check back soon.</div>}
      </div>
      <ClubJoinModal club={pick} onOpenChange={(o: boolean) => !o && setPick(null)} />
    </Card>
  );
}

function ClubJoinModal({ club, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [motivation, setMotivation] = useState("");
  const [experience, setExperience] = useState("beginner");
  const [consent, setConsent] = useState(false);
  const join = useMutation({
    mutationFn: async () => {
      if (!consent) throw new Error("Tick the commitment box");
      const { error } = await (supabase.from as any)("student_club_memberships").insert({
        club_id: club.id, student_user_id: user!.id, motivation, experience,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(`Welcome to ${club?.name}`);
      qc.invalidateQueries({ queryKey: ["student-club-memberships"] });
      onOpenChange(false); setMotivation(""); setConsent(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!club) return null;
  const Icon = clubIcon(club.category);
  return (
    <Dialog open={!!club} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0">
        <div className="relative overflow-hidden p-6 text-white" style={{ background: `linear-gradient(135deg, ${club.color_hex}, ${club.color_hex}aa 60%, #111)` }}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-44 w-44 rounded-full bg-white/15 blur-3xl" />
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="grid h-12 w-12 place-items-center rounded-2xl bg-white/20 backdrop-blur"><Icon className="h-6 w-6" /></div>
              <div>
                <DialogTitle className="text-white text-2xl">{club.name}</DialogTitle>
                <DialogDescription className="text-white/80">{club.category} · {club.location}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div className="relative mt-4 grid grid-cols-3 gap-2 text-center text-[10px]">
            <div className="rounded-xl bg-white/10 p-2 backdrop-blur"><Clock className="mx-auto h-3.5 w-3.5" /><div className="mt-1 opacity-80">Meets</div><div className="font-semibold">{club.meets_on}</div></div>
            <div className="rounded-xl bg-white/10 p-2 backdrop-blur"><Users className="mx-auto h-3.5 w-3.5" /><div className="mt-1 opacity-80">Coach</div><div className="font-semibold">{club.coach_name ?? "TBA"}</div></div>
            <div className="rounded-xl bg-white/10 p-2 backdrop-blur"><Trophy className="mx-auto h-3.5 w-3.5" /><div className="mt-1 opacity-80">Capacity</div><div className="font-semibold">{club.capacity}</div></div>
          </div>
        </div>
        <div className="space-y-3 p-5">
          <p className="text-xs text-muted-foreground">{club.description}</p>
          <div>
            <Label>Why do you want to join?</Label>
            <Textarea rows={3} value={motivation} onChange={e => setMotivation(e.target.value)} className="mt-1" placeholder="One line is fine." />
          </div>
          <div>
            <Label>Experience</Label>
            <Select value={experience} onValueChange={setExperience}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner — teach me</SelectItem>
                <SelectItem value="some">Some experience</SelectItem>
                <SelectItem value="advanced">Advanced / competitive</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-start gap-2 text-xs">
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)} className="mt-0.5" />
            <span>I commit to attending weekly practice and letting the coach know if I miss.</span>
          </label>
        </div>
        <DialogFooter className="px-5 pb-5">
          <Button onClick={() => join.mutate()} disabled={join.isPending || !consent} className="w-full">
            {join.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Sparkles className="mr-1 h-3 w-3" />}
            Join {club.name}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MEALS — with order modal
// ═════════════════════════════════════════════════════════════════════════════

const DEFAULT_MENU = {
  Breakfast: ["Katogo", "Pancakes & tea", "Porridge & bread"],
  Break:     ["Fresh fruit", "Roasted maize", "Peanuts"],
  Lunch:     ["Rice, beans & greens", "Matoke, groundnut sauce", "Chapati & meat stew"],
  Dinner:    ["Posho & fish", "Spaghetti bolognese", "Rice & chicken curry"],
};

export function MealsServiceCardV2() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const { data: orders = [] } = useQuery({
    queryKey: ["student-meal-orders", user?.id, today],
    enabled: !!user?.id,
    queryFn: async () => (await (supabase.from as any)("student_meal_orders").select("*").eq("student_user_id", user!.id).eq("meal_date", today)).data ?? [],
  });
  const bySlot = Object.fromEntries((orders || []).map((o: any) => [o.meal_slot, o]));
  const [pick, setPick] = useState<{ slot: string; time: string } | null>(null);
  const slots = [
    { slot: "Breakfast", t: "07:00", Icon: Coffee },
    { slot: "Break",     t: "10:30", Icon: Leaf   },
    { slot: "Lunch",     t: "13:00", Icon: Utensils },
    { slot: "Dinner",    t: "19:00", Icon: Utensils },
  ];
  return (
    <Card className="overflow-hidden">
      <div className="border-b p-4" style={{ background: "linear-gradient(120deg, hsl(var(--primary)/0.08), transparent)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-lg"><Utensils className="h-4 w-4 text-primary" />Meals</div>
          <Badge variant="secondary" className="text-[10px]">Included in plan</Badge>
        </div>
      </div>
      <div className="space-y-2 p-4 text-xs">
        {slots.map(s => {
          const o = bySlot[s.slot];
          return (
            <div key={s.slot} className="flex items-center justify-between rounded-lg border p-2.5">
              <div className="flex items-center gap-2">
                <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary"><s.Icon className="h-3.5 w-3.5" /></div>
                <div>
                  <div className="font-semibold">{s.slot} <span className="ml-1 font-mono text-muted-foreground">{s.t}</span></div>
                  <div className="text-[10px] text-muted-foreground">{o ? o.item_label : `Tap to choose from ${DEFAULT_MENU[s.slot as keyof typeof DEFAULT_MENU].length} options`}</div>
                </div>
              </div>
              {o
                ? <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15"><CheckCircle2 className="mr-1 h-3 w-3" />Ordered</Badge>
                : <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={() => setPick({ slot: s.slot, t: s.t } as any)}>Choose</Button>}
            </div>
          );
        })}
      </div>
      <MealOrderModal pick={pick} onOpenChange={(o: boolean) => !o && setPick(null)} />
    </Card>
  );
}

function MealOrderModal({ pick, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [choice, setChoice] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => { if (pick) setChoice(""); }, [pick]);
  const submit = useMutation({
    mutationFn: async () => {
      if (!choice) throw new Error("Pick a dish");
      const { error } = await (supabase.from as any)("student_meal_orders").insert({
        student_user_id: user!.id, meal_slot: pick.slot, item_label: choice, note,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Meal reserved"); qc.invalidateQueries({ queryKey: ["student-meal-orders"] }); onOpenChange(false); },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!pick) return null;
  const menu = DEFAULT_MENU[pick.slot as keyof typeof DEFAULT_MENU] || [];
  return (
    <Dialog open={!!pick} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Choose your {pick.slot.toLowerCase()}</DialogTitle>
          <DialogDescription>Served around {pick.t}. Change up to 30 min before.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {menu.map(m => (
            <button key={m} onClick={() => setChoice(m)}
              className={`flex items-center justify-between rounded-xl border p-3 text-left text-sm transition ${choice === m ? "border-primary bg-primary/10" : "hover:border-primary/40"}`}>
              <span>{m}</span>{choice === m && <CheckCircle2 className="h-4 w-4 text-primary" />}
            </button>
          ))}
        </div>
        <div>
          <Label>Allergy / note (optional)</Label>
          <Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} className="mt-1" />
        </div>
        <DialogFooter>
          <Button onClick={() => submit.mutate()} disabled={submit.isPending}><CheckCircle2 className="mr-1 h-3 w-3" />Reserve</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// LIBRARY & LAB — with booking
// ═════════════════════════════════════════════════════════════════════════════

export function LibraryLabCardV2() {
  const { user } = useAuth();
  const { data: bookings = [] } = useQuery({
    queryKey: ["student-library-bookings", user?.id],
    enabled: !!user?.id,
    queryFn: async () => (await (supabase.from as any)("library_lab_bookings").select("*").eq("student_user_id", user!.id).order("slot_start", { ascending: false }).limit(6)).data ?? [],
  });
  const [pick, setPick] = useState<string | null>(null);
  const items = [
    { key: "Library",       Icon: Library,      pass: true  },
    { key: "Chemistry lab", Icon: FlaskConical, pass: false },
    { key: "Study lounge",  Icon: PlayCircle,   pass: false, note: "Open till 21:00" },
    { key: "Maker space",   Icon: Lightbulb,    pass: false },
    { key: "Music room",    Icon: Music,        pass: false },
    { key: "Sports field",  Icon: Trophy,       pass: false },
  ];
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center gap-2 font-display text-lg"><Library className="h-4 w-4 text-primary" />Library & lab</div>
      <div className="grid gap-2 text-xs">
        {items.map(i => (
          <div key={i.key} className="flex items-center justify-between rounded-lg border p-2">
            <div className="flex items-center gap-2"><i.Icon className="h-3.5 w-3.5" />{i.key}</div>
            {i.pass ? <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">Active</Badge>
              : i.note ? <span className="text-[10px] text-muted-foreground">{i.note}</span>
              : <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setPick(i.key)}>Book</Button>}
          </div>
        ))}
      </div>
      {bookings.length > 0 && (
        <div className="mt-3 rounded-lg border bg-muted/30 p-2">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">My bookings</div>
          <div className="mt-1 space-y-1 text-[11px]">
            {bookings.slice(0, 3).map((b: any) => (
              <div key={b.id} className="flex items-center justify-between">
                <span>{b.resource}</span>
                <span className="font-mono text-muted-foreground">{shortDateTime(b.slot_start)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <BookResourceModal pick={pick} onOpenChange={(o: boolean) => !o && setPick(null)} />
    </Card>
  );
}

function BookResourceModal({ pick, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [date, setDate] = useState(new Date().toISOString().slice(0,10));
  const [time, setTime] = useState("15:00");
  const [duration, setDuration] = useState("60");
  const [purpose, setPurpose] = useState("");
  const submit = useMutation({
    mutationFn: async () => {
      const start = new Date(`${date}T${time}:00`);
      const end = new Date(start.getTime() + Number(duration) * 60000);
      const { error } = await (supabase.from as any)("library_lab_bookings").insert({
        student_user_id: user!.id, resource: pick, slot_start: start.toISOString(), slot_end: end.toISOString(), purpose,
      });
      if (error) throw error;
    },
    onSuccess: () => { toast.success("Booked ✓"); qc.invalidateQueries({ queryKey: ["student-library-bookings"] }); onOpenChange(false); setPurpose(""); },
    onError: (e: Error) => toast.error(e.message),
  });
  if (!pick) return null;
  return (
    <Dialog open={!!pick} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Book {pick}</DialogTitle>
          <DialogDescription>Pick a time slot — confirmation is instant.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input type="date" value={date} onChange={e => setDate(e.target.value)} className="mt-1" /></div>
          <div><Label>Start</Label><Input type="time" value={time} onChange={e => setTime(e.target.value)} className="mt-1" /></div>
          <div className="col-span-2">
            <Label>Duration</Label>
            <Select value={duration} onValueChange={setDuration}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1h 30</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Purpose (optional)</Label><Textarea rows={2} value={purpose} onChange={e => setPurpose(e.target.value)} className="mt-1" /></div>
        </div>
        <DialogFooter><Button onClick={() => submit.mutate()} disabled={submit.isPending}><ClipboardCheck className="mr-1 h-3 w-3" />Confirm booking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SERVICES HUB — new request dialog + tiles + document uploads
// ═════════════════════════════════════════════════════════════════════════════

const REQUEST_TYPES = [
  { k: "permission",    label: "Permission slip",   Icon: FileText,     hue: "#7B1416", desc: "Leave campus / event / trip" },
  { k: "sick_leave",    label: "Sick leave",        Icon: HeartPulse,   hue: "#B7355A", desc: "Excuse absence from class"   },
  { k: "laundry",       label: "Laundry",           Icon: Cloud,        hue: "#5B4B8A", desc: "Weekly pickup / express"     },
  { k: "tuck_shop",     label: "Tuck-shop order",   Icon: Utensils,     hue: "#C1553A", desc: "Snacks & essentials"          },
  { k: "id_replace",    label: "Replace ID / card", Icon: CreditCard,   hue: "#0ea5e9", desc: "Lost / damaged card"          },
  { k: "transport",     label: "Transport change",  Icon: Bus,          hue: "#7B1416", desc: "Different route / stop"       },
  { k: "counsellor",    label: "Book counsellor",   Icon: Brain,        hue: "#8B5CF6", desc: "Confidential session"         },
  { k: "device_repair", label: "Device repair",     Icon: Wrench,       hue: "#111114", desc: "Laptop / tablet fix"          },
  { k: "book_return",   label: "Book / return",     Icon: BookOpen,     hue: "#F6A85E", desc: "Library extension / return"   },
  { k: "docs_upload",   label: "Upload document",   Icon: Upload,       hue: "#0891b2", desc: "Reports, letters, forms"      },
  { k: "custom",        label: "Something else",    Icon: Sparkles,     hue: "#5B4B8A", desc: "Free-form request"            },
];

export function ServicesHub() {
  const { user } = useAuth();
  const [type, setType] = useState<string | null>(null);
  const { data: reqs = [] } = useQuery({
    queryKey: ["student-service-requests", user?.id],
    enabled: !!user?.id,
    refetchInterval: 8000,
    queryFn: async () => (await (supabase.from as any)("student_service_requests").select("*").eq("student_user_id", user!.id).order("created_at", { ascending: false }).limit(30)).data ?? [],
  });
  const { data: docs = [] } = useQuery({
    queryKey: ["student-service-docs", user?.id],
    enabled: !!user?.id,
    queryFn: async () => (await (supabase.from as any)("student_service_documents").select("*").eq("student_user_id", user!.id).order("created_at", { ascending: false }).limit(20)).data ?? [],
  });
  return (
    <>
      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="font-display text-lg">Services</div>
          <Button size="sm" onClick={() => setType("custom")}><Plus className="mr-1 h-3 w-3" />New request</Button>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
          {REQUEST_TYPES.map(t => (
            <button key={t.k} onClick={() => setType(t.k)}
              className="group relative overflow-hidden rounded-xl border p-3 text-left transition hover:-translate-y-0.5 hover:shadow-md"
              style={{ background: `linear-gradient(135deg, ${t.hue}0d, transparent 70%)` }}>
              <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: `${t.hue}22`, color: t.hue }}>
                <t.Icon className="h-4 w-4" />
              </div>
              <div className="mt-2 text-xs font-semibold">{t.label}</div>
              <div className="text-[10px] text-muted-foreground">{t.desc}</div>
            </button>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-display text-base">My requests</div>
            <Badge variant="secondary" className="text-[10px]">{reqs.length} total</Badge>
          </div>
          {reqs.length === 0 && <div className="rounded-lg bg-muted/40 p-6 text-center text-xs text-muted-foreground">No requests yet — tap a tile above.</div>}
          <div className="space-y-2">
            {reqs.slice(0, 8).map((r: any) => {
              const T = REQUEST_TYPES.find(t => t.k === r.request_type) || REQUEST_TYPES[REQUEST_TYPES.length - 1];
              const Icon = r.status === "resolved" ? CheckCircle2 : r.status === "rejected" ? XCircle : Hourglass;
              const color = r.status === "resolved" ? "text-emerald-600" : r.status === "rejected" ? "text-destructive" : "text-amber-600";
              return (
                <div key={r.id} className="flex items-start gap-3 rounded-xl border p-3">
                  <div className="grid h-9 w-9 place-items-center rounded-lg" style={{ background: `${T.hue}18`, color: T.hue }}>
                    <T.Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="truncate text-sm font-semibold">{r.title}</div>
                      <div className={`flex items-center gap-1 text-[10px] ${color}`}><Icon className="h-3 w-3" /><span className="capitalize">{r.status}</span></div>
                    </div>
                    {r.body && <div className="mt-0.5 line-clamp-1 text-[11px] text-muted-foreground">{r.body}</div>}
                    <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">{shortDateTime(r.created_at)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>

        <Card className="p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="font-display text-base">My documents</div>
            <Badge variant="secondary" className="text-[10px]">{docs.length}</Badge>
          </div>
          {docs.length === 0 && <div className="rounded-lg bg-muted/40 p-4 text-center text-xs text-muted-foreground">Uploaded documents will appear here.</div>}
          <div className="space-y-1.5">
            {docs.slice(0, 8).map((d: any) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border p-2 text-xs">
                <div className="flex min-w-0 items-center gap-2">
                  <Paperclip className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span className="truncate">{d.file_name}</span>
                </div>
                <span className="ml-2 shrink-0 text-[10px] text-muted-foreground">{Math.round((d.size_bytes || 0) / 1024)} KB</span>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" className="mt-3 w-full" onClick={() => setType("docs_upload")}>
            <Upload className="mr-1 h-3 w-3" />Upload document
          </Button>
        </Card>
      </div>

      <NewServiceRequestDialog typeKey={type} onOpenChange={(o: boolean) => !o && setType(null)} />
    </>
  );
}

function NewServiceRequestDialog({ typeKey, onOpenChange }: any) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [priority, setPriority] = useState("normal");
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (typeKey) {
      const t = REQUEST_TYPES.find(x => x.k === typeKey);
      setTitle(t ? t.label : ""); setBody(""); setFiles([]); setPriority("normal");
    }
  }, [typeKey]);

  const submit = useMutation({
    mutationFn: async () => {
      if (!title) throw new Error("Give it a title");
      const { data: req, error } = await (supabase.from as any)("student_service_requests").insert({
        student_user_id: user!.id, request_type: typeKey, title, body, priority,
      }).select().single();
      if (error) throw error;
      for (const f of files) {
        const path = `${user!.id}/${typeKey}/${req.id}/${Date.now()}-${f.name}`;
        const up = await supabase.storage.from("student-documents").upload(path, f);
        if (up.error) throw up.error;
        await (supabase.from as any)("student_service_documents").insert({
          student_user_id: user!.id, request_id: req.id, doc_kind: typeKey,
          file_name: f.name, storage_path: path, mime_type: f.type, size_bytes: f.size,
        });
      }
    },
    onSuccess: () => {
      toast.success("Submitted");
      qc.invalidateQueries({ queryKey: ["student-service-requests"] });
      qc.invalidateQueries({ queryKey: ["student-service-docs"] });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const T = REQUEST_TYPES.find(t => t.k === typeKey);
  if (!typeKey || !T) return null;
  return (
    <Dialog open={!!typeKey} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg overflow-hidden p-0">
        <div className="relative overflow-hidden p-5 text-white" style={{ background: `linear-gradient(135deg, ${T.hue}, ${T.hue}aa 60%, #111)` }}>
          <div className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/15 blur-3xl" />
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="grid h-11 w-11 place-items-center rounded-2xl bg-white/20 backdrop-blur"><T.Icon className="h-5 w-5" /></div>
              <div>
                <DialogTitle className="text-white">{T.label}</DialogTitle>
                <DialogDescription className="text-white/80">{T.desc}</DialogDescription>
              </div>
            </div>
          </DialogHeader>
        </div>
        <div className="space-y-3 p-5">
          <div><Label>Title</Label><Input value={title} onChange={e => setTitle(e.target.value)} className="mt-1" /></div>
          <div><Label>Details</Label><Textarea rows={4} value={body} onChange={e => setBody(e.target.value)} className="mt-1" placeholder="Add any context so the office can help fast." /></div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <FileDropZone files={files} setFiles={setFiles} />
        </div>
        <DialogFooter className="px-5 pb-5">
          <Button className="w-full" onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FileDropZone({ files, setFiles }: { files: File[]; setFiles: (f: File[]) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <div>
      <Label>Attachments (optional)</Label>
      <div
        onClick={() => ref.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); setFiles([...files, ...Array.from(e.dataTransfer.files)]); }}
        className="mt-1 cursor-pointer rounded-xl border-2 border-dashed border-muted-foreground/30 p-4 text-center text-xs text-muted-foreground transition hover:border-primary hover:bg-primary/5"
      >
        <Upload className="mx-auto mb-1 h-5 w-5" />
        <div>Click or drop files (PDF, images, docs)</div>
        <input ref={ref} type="file" multiple hidden onChange={e => e.target.files && setFiles([...files, ...Array.from(e.target.files)])} />
      </div>
      {files.length > 0 && (
        <div className="mt-2 space-y-1">
          {files.map((f, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg border bg-muted/40 p-1.5 text-[11px]">
              <div className="flex items-center gap-1.5"><Paperclip className="h-3 w-3" /><span className="truncate">{f.name}</span></div>
              <button onClick={() => setFiles(files.filter((_, j) => j !== i))}><Trash2 className="h-3 w-3 text-destructive" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// FUNDRED PAY CARD — physical card carousel
// ═════════════════════════════════════════════════════════════════════════════

export function FundredPayCardShowcase({ wallet }: { wallet: any }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [flipped, setFlipped] = useState(false);
  const [qr, setQr] = useState("");
  const { data: card } = useQuery({
    queryKey: ["student-card", user?.id],
    enabled: !!user?.id,
    queryFn: async () => (await (supabase.from as any)("student_payment_cards").select("*").eq("student_user_id", user!.id).maybeSingle()).data,
  });
  useEffect(() => { if (wallet?.id) QRCode.toDataURL(wallet.id, { margin: 1, width: 240, color: { dark: "#0F2E21", light: "#FBFAF5" } }).then(setQr); }, [wallet?.id]);
  const request = useMutation({
    mutationFn: async () => {
      const cardNo = "FP-" + String(Date.now()).slice(-10);
      const { error } = await (supabase.from as any)("student_payment_cards").insert({
        student_user_id: user!.id, wallet_id: wallet?.id, card_no: cardNo, tier_label: "FP1000",
      });
      if (error) throw error;
      await (supabase.from as any)("student_service_requests").insert({
        student_user_id: user!.id, request_type: "id_replace", title: "Physical Fundred Pay card",
        body: `Please issue physical card ${cardNo}`,
      });
    },
    onSuccess: () => { toast.success("Card requested — pick it up at the office"); qc.invalidateQueries({ queryKey: ["student-card"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const displayNo = card ? card.card_no : "FP • • • •  • • • •  • • • •  • • • •";
  const holder = (user?.user_metadata?.full_name ?? user?.email ?? "STUDENT").toUpperCase();

  return (
    <Card className="overflow-hidden">
      <div className="grid gap-4 p-4 lg:grid-cols-[1.1fr_1fr]">
        {/* Card visual, styled after the physical Fundred Pay card */}
        <div className="[perspective:1400px]">
          <motion.div
            className="relative aspect-[85/54] w-full [transform-style:preserve-3d]"
            animate={{ rotateY: flipped ? 180 : 0 }} transition={{ duration: 0.7 }}
          >
            {/* Front */}
            <div className="absolute inset-0 [backface-visibility:hidden] rounded-2xl overflow-hidden shadow-2xl">
              <img src={fundredCard} alt="Fundred Pay card" className="absolute inset-0 h-full w-full object-cover" />
              {/* Overlay dynamic details on top of the printed art */}
              <div className="absolute inset-0 p-5 text-white/95">
                <div className="flex items-start justify-between">
                  <div className="text-[9px] uppercase tracking-[0.28em] opacity-80">Student Card</div>
                  <div className="text-[9px] uppercase tracking-[0.28em] opacity-80">{card?.tier_label ?? "FP1000"}</div>
                </div>
                <div className="absolute bottom-16 left-5 right-5">
                  <div className="font-mono text-[15px] tracking-[0.28em] drop-shadow">{displayNo}</div>
                </div>
                <div className="absolute bottom-4 left-5 right-5 flex items-end justify-between text-[10px] uppercase tracking-widest">
                  <div>
                    <div className="opacity-60">Cardholder</div>
                    <div className="font-semibold">{holder.slice(0, 22)}</div>
                  </div>
                  <div className="text-right">
                    <div className="opacity-60">Status</div>
                    <div className="font-semibold">{card?.status ?? "not issued"}</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Back — QR + wallet id */}
            <div className="absolute inset-0 [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-2xl overflow-hidden text-white shadow-2xl"
              style={{ background: "linear-gradient(135deg,#0F2E21,#7B1416 60%,#0F2E21)" }}>
              <div className="absolute top-6 left-0 right-0 h-10 bg-black/70" />
              <div className="absolute inset-0 grid place-items-center">
                <div className="text-center">
                  <div className="rounded-xl bg-white p-2">
                    {qr ? <img src={qr} alt="Pay" className="h-24 w-24" /> : <QrCode className="h-24 w-24 text-black" />}
                  </div>
                  <div className="mt-2 font-mono text-[10px] opacity-80">CVV ••• · TriSphere Pay</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Details */}
        <div className="flex flex-col">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
            <CreditCard className="h-3 w-3" />Physical card
          </div>
          <div className="mt-1 font-display text-2xl">Fundred Pay</div>
          <div className="mt-1 text-xs text-muted-foreground">
            Tap-to-pay across every school-approved merchant, canteen and library. Same wallet, same limits, same protection.
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px]">
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Daily limit</div><div className="font-mono font-semibold">{fmtUGX(card?.daily_limit_ugx ?? 50000)}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Tier</div><div className="font-semibold">{card?.tier_label ?? "FP1000"}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Issued</div><div className="font-mono">{card?.issued_at ? new Date(card.issued_at).toLocaleDateString() : "—"}</div></div>
            <div className="rounded-lg border p-2"><div className="text-[10px] text-muted-foreground">Status</div><div className="font-semibold capitalize">{card?.status ?? "not issued"}</div></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setFlipped(f => !f)}><QrCode className="mr-1 h-3 w-3" />{flipped ? "Show front" : "Show QR back"}</Button>
            {!card
              ? <Button size="sm" onClick={() => request.mutate()} disabled={request.isPending}><Sparkles className="mr-1 h-3 w-3" />Request card</Button>
              : <Button size="sm" variant="outline"><Lock className="mr-1 h-3 w-3" />Freeze card</Button>}
          </div>
          <div className="mt-3 rounded-lg border bg-muted/30 p-2 text-[11px]">
            <div className="flex items-center gap-1.5 font-medium"><ShieldCheck className="h-3.5 w-3.5 text-primary" />Safe by design</div>
            <ul className="mt-1 list-disc pl-4 text-[10px] text-muted-foreground">
              <li>Only school-approved merchants can charge.</li>
              <li>Guardian gets a nudge on unusual purchases.</li>
              <li>Freeze instantly from the app if lost.</li>
            </ul>
          </div>
        </div>
      </div>
    </Card>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// STUDY LIBRARY (Learn tab body — DB-driven)
// ═════════════════════════════════════════════════════════════════════════════

export function StudyLibrary({ classLabel, schoolId, subjectMap }: any) {
  const [pick, setPick] = useState<any>(null);
  const [q, setQ] = useState("");
  const { data: resources = [], isLoading } = useQuery({
    queryKey: ["learning-resources", schoolId, classLabel],
    queryFn: async () => {
      let query = (supabase.from as any)("learning_resources").select("*").order("created_at", { ascending: false }).limit(60);
      if (schoolId) query = query.eq("school_id", schoolId);
      const { data } = await query;
      return data ?? [];
    },
  });
  const filtered = (resources as any[]).filter(r => !q || r.title.toLowerCase().includes(q.toLowerCase()));
  const kinds: Record<string, any> = {
    notes: FileText, video: Video, worksheet: ClipboardCheck, past_paper: BookOpen, link: ExternalLink,
  };
  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-display text-lg"><Library className="h-4 w-4 text-primary" />Study library</div>
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search notes, papers…" className="h-8 max-w-[220px]" />
      </div>
      {isLoading && <div className="grid gap-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />)}</div>}
      {!isLoading && filtered.length === 0 && (
        <div className="rounded-xl border border-dashed p-6 text-center text-xs text-muted-foreground">
          Your teachers haven't posted resources yet. Notes, videos and past papers will show up here.
        </div>
      )}
      <div className="grid gap-2 md:grid-cols-2">
        {filtered.slice(0, 12).map(r => {
          const Icon = kinds[r.kind] || FileText;
          const subj = subjectMap?.[r.subject_id];
          return (
            <button key={r.id} onClick={() => setPick(r)} className="flex items-start gap-2 rounded-xl border p-3 text-left transition hover:shadow-md">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{r.title}</div>
                <div className="text-[10px] text-muted-foreground">{subj?.name ?? "General"} · {r.kind}</div>
              </div>
              <ChevronRight className="mt-1 h-4 w-4 text-muted-foreground" />
            </button>
          );
        })}
      </div>
      <Dialog open={!!pick} onOpenChange={(o: boolean) => !o && setPick(null)}>
        <DialogContent>
          {pick && (
            <>
              <DialogHeader>
                <DialogTitle>{pick.title}</DialogTitle>
                <DialogDescription>{pick.description || "Learning resource"}</DialogDescription>
              </DialogHeader>
              <div className="rounded-lg bg-muted/50 p-3 text-xs">
                <div><span className="text-muted-foreground">Type:</span> {pick.kind}</div>
                <div><span className="text-muted-foreground">Class:</span> {pick.class_label ?? "All"}</div>
                <div><span className="text-muted-foreground">Posted:</span> {shortDateTime(pick.created_at)}</div>
              </div>
              <DialogFooter>
                {pick.url && <a href={pick.url} target="_blank" rel="noreferrer"><Button><ExternalLink className="mr-1 h-3 w-3" />Open</Button></a>}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
